import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cn from 'classnames';
import { Select } from 'eone-ui/select/select';
import { TableAlignment } from 'eone-ui/tables/table-constants';
import { isNil } from 'lodash-es';
import { useCallback, useMemo } from 'react';

const emptyValueSet = new Set(['', null, undefined]);

function TableRowSelect(props) {
  // Note: destructing props to fix warning: React Hook useCallback has a missing dependency: 'props'...
  // the preferred fix is to destructure the 'props' object outside of... -- react-hooks/exhaustive-deps
  const { FieldName, ChangeValue, UpdateValue } = props;

  const changeValue = useCallback(
    (value) => {
      if (typeof ChangeValue === 'function') {
        ChangeValue(FieldName, value);
      }
    },
    [FieldName, ChangeValue],
  );

  const updateValue = useCallback(
    (value) => {
      if (typeof UpdateValue === 'function') {
        UpdateValue(FieldName, value);
      }
    },
    [FieldName, UpdateValue],
  );

  const changeAndUpdateValue = useCallback(
    (value) => {
      changeValue(value);
      updateValue(value);
    },
    [changeValue, updateValue],
  );

  const options = useMemo(() => {
    if (props.Options) {
      if (Array.isArray(props.Options)) {
        return props.Options;
      }
      if (props.Options in props.Row) {
        return props.Row[props.Options];
      }
    }
    return [];
  }, [props.Row, props.Options]);

  const getOptionValue = (options, key) => {
    for (let i = 0, len = options.length; i < len; i++) {
      if (options[i].Key === key) {
        return options[i].Value;
      }
    }
    return key;
  };

  const getSelectLabelValue = (value) => {
    return getOptionValue(options, value);
  };

  const getLabelValue = (value, isArray) => {
    if (isArray) {
      if (!value) return '';
      const labels: any[] = [];
      for (let i = 0, len = value.length; i < len; i++) {
        labels.push(getLabelValue(value[i], false));
      }
      labels.sort();
      return labels.join(', ');
    }
    return getSelectLabelValue(value);
  };

  const getOptionIcon = (key) => {
    for (let i = 0, len = options.length; i < len; i++) {
      if (options[i].Key === key) {
        return options[i].Icon;
      }
    }
    return null;
  };

  const value = props.Row[props.FieldName];

  /* START OF option-value validation */
  const optionSet = useMemo(() => {
    // Note: use Key (the stored value) for validation, not Value (the display label).
    // Using Value was a latent bug that only worked when label === key (e.g. source columns).
    // For options where key ≠ label (e.g. NAV OData enum params where key="2", label="Open"),
    // the old check always produced isOptionInvalid=true causing a spurious red border.
    return new Set(options?.map((item) => item.Key));
  }, [options]);
  // Important Note: success-prop option-value validation, instead of loading-prop,
  // because loading-prop is false initially and cannot judge
  const isEmptyValue = emptyValueSet.has(value);
  const isRequiredButEmpty = props.Required && isEmptyValue;
  const isOptionalAndEmpty = !props.Required && isEmptyValue;
  const isOptionInvalid = !isOptionalAndEmpty && props.Success && !optionSet.has(value);
  const hasError = isRequiredButEmpty || isOptionInvalid;
  /* END OF option-value validation */

  const getClass = () => {
    let className = 'eone-ui-table-row-data';
    if (hasError) className += ' sc-form-field-has-error';
    switch (props.Alignment) {
      case TableAlignment.Center:
        className = `${className} eone-ui-table-row-data-center`;
        break;
      case TableAlignment.Right:
        className = `${className} eone-ui-table-row-data-right`;
        break;
    }
    return className;
  };

  if (props.Editable) {
    const filteredOptions = useMemo(() => {
      if (!props.SelectedOptions?.length) return options;
      return options
        .slice()
        .filter(
          (option) => option.Value === value || !props.SelectedOptions.includes(option.Value),
        );
    }, [value, options, props.SelectedOptions]);
    const useSuccessForLoading = !isNil(props.Success) && !props.Success; // e.g. success=false
    const loading = props.Loading || useSuccessForLoading;
    return (
      <Select
        Placeholder={props.Placeholder}
        LabelRender={props.LabelRender}
        MultiSelect={props.Array}
        Options={filteredOptions}
        ReadOnly={props.ReadOnly}
        Value={value}
        ContainerClass={cn({
          'eone-ui-input-table-select': true,
          'sc-form-field-disabled': props.ReadOnly,
          'sc-form-field-has-error': hasError,
        })}
        Search={props.ShowSearch}
        Loading={loading}
        OnChange={changeAndUpdateValue}
      />
    );
  }

  const label = getLabelValue(value, props.Array);
  const icon = getOptionIcon(value);

  if (icon) {
    return (
      <div title={label} className={getClass()}>
        <span className="eone-ui-select-icon">
          <FontAwesomeIcon icon={icon} />
        </span>
        <span>{label}</span>
      </div>
    );
  }

  return (
    <div title={label} className={getClass()}>
      {label}
    </div>
  );
}

export { TableRowSelect };
